import { IApiResponse } from '@shared/interface/api-response.interface';
import { IUser } from '@shared/interface/model.interface';
import endpoints from '@shared/service/endpoint/endpoints.class';
import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut
} from './common/api';
import { ApiStatusEnum } from '@shared/enum/api-enum';
import { ApiError } from '@shared/error/api.error';
import { manageApiError } from './common/api.lib';

/**
 * Get all the users
 * @returns
 */
export const apiGetUsers = (): Promise<IUser[] | undefined> => {
  const endpointClass = endpoints.admin.getUsers;
  return apiGet(endpointClass.getUrl(), {
    publicApi: endpointClass.isPublic(),
  })
    .then((response: IApiResponse<IUser[], string>) => {
      if (
        response.returnCode === 'ADMIN_USERS_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        return response.returnSuccess;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) => manageApiError(error, 'apiGetUsers'));
};

/**
 * Update user's roles
 * @param user
 * @returns
 */
export const apiPutUpdateUserRoles = (
  user: IUser,
): Promise<IApiResponse<string, string> | undefined> => {
  const payload = {
    uuid: user.uuid ?? '',
    roles: user.roles ?? []
  };
  return apiPut(endpoints.admin.putUpdateUserRoles.getUrl(), payload)
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnStatus === ApiStatusEnum.SUCCESS &&
        ['ADMIN_UPDATE_USER_OK', 'ADMIN_UPDATE_USER_OK_NO_MAIL'].includes(
          response.returnCode,
        )
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) =>
      manageApiError(error, 'apiPutUpdateUserRoles'),
    );
};

/**
 * Add a user
 * @param email
 * @returns
 */
export const apiAddUser = (
  username?: string,
  email?: string,
): Promise<IApiResponse<string, string>> => {
  const payload = {
    username: username ?? '',
    email: email ?? '',
  };
  const endpointClass = endpoints.admin.postAddUser;
  return apiPost(endpointClass.getUrl(), payload, {
    publicApi: endpointClass.isPublic(),
  })
    .then((response: IApiResponse<string, string>) => {
      if (
        ['ADMIN_ADD_USER_OK_1', 'ADMIN_ADD_USER_OK_2'].includes(
          response.returnCode,
        ) &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) => manageApiError(error, 'apiAddUser'));
};

/**
 * Renew the password of a user
 * @param email
 * @returns
 */
export const apiPasswordRenew = (
  email?: string,
): Promise<IApiResponse<string, string>> => {
  const payload = {
    email: email ?? '',
  };
  const endpointClass = endpoints.admin.postPasswordRenew;
  return apiPost(endpointClass.getUrl(), payload, {
    publicApi: endpointClass.isPublic(),
  })
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnCode === 'ADMIN_PWD_RENEW_USER_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) =>
      manageApiError(error, 'apiPasswordRenew'),
    );
};

/**
 * Renew the password of a user
 * @param email
 * @returns
 */
export const apiResendValidationLink = (
  uuid?: string,
): Promise<IApiResponse<string, string>> => {
  const payload = {
    uuid: uuid ?? '',
  };
  const endpointClass = endpoints.admin.postResendValidationLink;
  return apiPost(endpointClass.getUrl(), payload, {
    publicApi: endpointClass.isPublic(),
  })
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnCode === 'ADMIN_RESEND_VALIDATION_LINK_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        return response;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) =>
      manageApiError(error, 'apiResendValidationLink'),
    );
};

/**
 * Delete user
 * @param email
 * @returns
 */
export const apiDeleteUser = (uuid?: string): Promise<string | undefined> => {
  return apiDelete(
    endpoints.admin.deleteUser.addParams([uuid ?? 'unknown']).getUrl(),
  )
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnCode === 'ADMIN_DELETE_USER_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        return response.returnSuccess;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) => manageApiError(error, 'apiDeleteUser'));
};

/**
 * Update user
 * @param email
 * @returns
 */
export const apiUpdateUser = (user: IUser): Promise<string | undefined> => {
  const payload = {
    uuid: user.uuid ?? '',
    username: user.username ?? '',
    status: user.status,
  };
  return apiPut(endpoints.admin.putUpdateUser.getUrl(), payload)
    .then((response: IApiResponse<string, string>) => {
      if (
        response.returnCode === 'ADMIN_UPDATE_USER_OK' &&
        response.returnStatus === ApiStatusEnum.SUCCESS
      ) {
        return response.returnSuccess;
      } else {
        throw new ApiError(response.returnError ?? '', response.returnCode);
      }
    })
    .catch((error: ErrorConstructor) => manageApiError(error, 'apiUpdateUser'));
};
